import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { CBadge, CSmartTable } from '@coreui/react-pro'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthContext } from 'src/contexts/authContext'

import dateFormat from 'dateformat'

import { tenderOverviewColumns } from './tableColumns/tenderOverviewColumns'
import { getAllTenders } from 'src/services/requests/tenders/tenders'

const TenderOverview = () => {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const auth = useContext(AuthContext)

  const columns = tenderOverviewColumns()

  const [tenders, setTenders] = useState([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState([])
  const [intervalCount, setIntervalCount] = useState(0)

  const fetchTenders = async () => {
    setIsLoading(true)
    getAllTenders().then((result) => {
      result.data.totalRecords ? setTenders(result.data.records) : setTenders([])
      setIsLoading(false)
    })
  }

  const getBadge = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'danger'
      case 'created':
        return 'primary'
      case 'ready_for_user':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'emails_sent':
        return 'secondary'
      default:
        return 'primary'
    }
  }

  const navigateToTenderName = (tenderId) => {
    navigate('/tender-overview/tender-detail-view/' + tenderId)
  }

  useEffect(() => {
    fetchTenders()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.authStatus === 'SignedIn' && location.pathname === '/tender-overview') {
        window.location.reload()
      }
      setIntervalCount(intervalCount + 1)
    }, 300000)

    //Clearing the interval
    return () => clearInterval(interval)
  }, [intervalCount])

  return (
    <CSmartTable
      activePage={1}
      cleaner={false}
      clickableRows={true}
      selectable={false}
      footer={false}
      tableFilter={false}
      tableProps={{
        className: 'tender-overview-table',
        hover: true,
        responsive: true,
        bordered: true,
      }}
      tableBodyProps={{
        className: 'align-middle',
      }}
      columnFilter
      columnSorter
      sorterValue={{ column: 'uploadedAt', state: 'desc' }}
      loading={isLoading}
      columns={columns}
      items={tenders}
      itemsPerPage={itemsPerPage}
      itemsPerPageLabel={t('ItemsPerPage') + ':'}
      noItemsLabel={t('NoResultsFound')}
      itemsPerPageSelect
      pagination
      onActivePageChange={(activePage) => setActivePage(activePage)}
      onItemsPerPageChange={(itemsPerPage) => {
        setActivePage(1)
        setItemsPerPage(itemsPerPage)
      }}
      scopedColumns={{
        hgcSalesforceProject: (item) => (
          <td
            className={`tender-name ${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            {item.hgcSalesforceProject}
          </td>
        ),
        departmentName: (item) => (
          <td
            className={`${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            {item.departmentName}
          </td>
        ),
        uploaderUserName: (item) => (
          <td
            className={`${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            <span>{item.uploaderUserName}</span>
          </td>
        ),
        offerDueDate: (item) => (
          <td
            className={`${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            {item.offerDueDate === null ? ' ' : dateFormat(item.offerDueDate, 'dd.mm.yyyy')}
          </td>
        ),
        supplierDueDate: (item) => (
          <td
            className={`${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            {item.supplierDueDate === null ? ' ' : dateFormat(item.supplierDueDate, 'dd.mm.yyyy')}
          </td>
        ),
        uploadedAt: (item) => (
          <td
            className={`${item.status === 'in_progress' ? 'status--in-progress' : ''}`}
            onClick={() => item.active === 'active' && item.status !== 'in_progress' && navigateToTenderName(item.id)}
          >
            {dateFormat(item.uploadedAt, 'dd.mm.yyyy')}
          </td>
        ),
        status: (item) => (
          <td>
            <CBadge
              color={item.active === 'inactive' ? 'danger' : getBadge(item.status)}
              className={`w-100 py-2 text-uppercase ${item.status}`}
            >
              {item.active === 'inactive' ? t('TenderState.ProcessingFailed') : t(item.status)}
            </CBadge>
          </td>
        ),
      }}
    />
  )
}

export default TenderOverview
